import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, Eye, MousePointerClick, TrendingUp,
  Clock, Smartphone, Monitor, LogOut, Home,
  Tag, Plus, Edit2, Trash2, Check, X, Percent,
  Mail, MessageSquare, Phone, User, CheckCircle, Circle,
  ChevronLeft, ChevronRight, Star, Settings, Save,
  UtensilsCrossed, DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#D4AF37', '#0f172a', '#64748b', '#94a3b8', '#f59e0b', '#10b981'];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [periodData, setPeriodData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [customDate, setCustomDate] = useState('');
  
  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_percent: 10,
    description: '',
    is_unlimited: true,
    color_theme: 'gold',
    expires_at: ''
  });

  // Contact messages state
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Review settings state
  const [reviewSettings, setReviewSettings] = useState({ total_reviews: 273, average_rating: 4.6 });
  const [savingReviews, setSavingReviews] = useState(false);
  const [reviewsSaved, setReviewsSaved] = useState(false);

  // Menu state
  const [menuData, setMenuData] = useState(null);
  const [editingMenu, setEditingMenu] = useState(null);
  const [savingMenu, setSavingMenu] = useState(false);
  const [menuSaved, setMenuSaved] = useState(false);

  // Hours state
  const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  const [hoursData, setHoursData] = useState({
    lundi: '', mardi: '', mercredi: '', jeudi: '', vendredi: '', samedi: '', dimanche: ''
  });
  const [savingHours, setSavingHours] = useState(false);
  const [hoursSaved, setHoursSaved] = useState(false);

  useEffect(() => {
    loadAllAnalytics(period);
    loadPromoCodes();
    loadMessages();
    loadReviewSettings();
    loadMenuData();
    loadHoursData();
  }, []);

  useEffect(() => {
    if (!customDate) {
      loadAllAnalytics(period);
    }
  }, [period]);

  const selectPeriod = (p) => {
    setCustomDate('');
    setPeriod(p);
  };

  const selectCustomDate = (date) => {
    setCustomDate(date);
    setPeriod('');
    loadCustomDayAnalytics(date);
  };

  const loadCustomDayAnalytics = async (date) => {
    try {
      const startDate = `${date}T00:00:00`;
      const endDate = `${date}T23:59:59`;

      const response = await axios.get(`${API}/analytics/admin/overview`, {
        params: { start_date: startDate, end_date: endDate },
        withCredentials: true
      });

      setAnalytics(response.data);
      setPeriodData(null);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const loadAllAnalytics = async (selectedPeriod) => {
    try {
      const now = new Date();
      let startDate;
      
      if (selectedPeriod === 'daily') {
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
      } else if (selectedPeriod === 'weekly') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
      } else if (selectedPeriod === 'monthly') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
      } else {
        startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() - 1);
      }

      const [overviewRes, periodRes] = await Promise.all([
        axios.get(`${API}/analytics/admin/overview`, {
          params: {
            start_date: startDate.toISOString(),
            end_date: now.toISOString()
          },
          withCredentials: true
        }),
        axios.get(`${API}/analytics/admin/period/${selectedPeriod}`, {
          withCredentials: true
        })
      ]);

      setAnalytics(overviewRes.data);
      setPeriodData(periodRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const loadPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/promos/`, {
        withCredentials: true
      });
      setPromoCodes(response.data.promos || []);
    } catch (error) {
      console.error('Error loading promo codes:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact/messages`, {
        withCredentials: true
      });
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadReviewSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/reviews`);
      setReviewSettings(response.data);
    } catch (error) {
      console.error('Error loading review settings:', error);
    }
  };

  const loadMenuData = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenuData(response.data);
      setEditingMenu(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const saveMenuData = async () => {
    setSavingMenu(true);
    try {
      await axios.put(`${API}/menu`, editingMenu, { withCredentials: true });
      setMenuData(JSON.parse(JSON.stringify(editingMenu)));
      setMenuSaved(true);
      setTimeout(() => setMenuSaved(false), 3000);
    } catch (error) {
      console.error('Error saving menu:', error);
      alert('Erreur lors de la sauvegarde du menu');
    } finally {
      setSavingMenu(false);
    }
  };

  const loadHoursData = async () => {
    try {
      const response = await axios.get(`${API}/settings/hours`);
      setHoursData(response.data.hours || {});
    } catch (error) {
      console.error('Error loading hours:', error);
    }
  };

  const saveHoursData = async () => {
    setSavingHours(true);
    try {
      await axios.put(`${API}/settings/hours`, { hours: hoursData }, { withCredentials: true });
      setHoursSaved(true);
      setTimeout(() => setHoursSaved(false), 3000);
    } catch (error) {
      console.error('Error saving hours:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingHours(false);
    }
  };


  const updateMenuPrice = (category, index, field, value) => {
    const updated = { ...editingMenu };
    if (index !== null && index !== undefined) {
      if (Array.isArray(updated[category])) {
        updated[category] = [...updated[category]];
        updated[category][index] = { ...updated[category][index], [field]: parseFloat(value) || 0 };
      } else if (updated[category]?.formats && field.startsWith('formats.')) {
        const fIdx = parseInt(field.split('.')[1]);
        updated[category] = { ...updated[category] };
        updated[category].formats = [...updated[category].formats];
        updated[category].formats[fIdx] = { ...updated[category].formats[fIdx], price: parseFloat(value) || 0 };
      }
    } else {
      updated[category] = { ...updated[category], [field]: parseFloat(value) || 0 };
    }
    setEditingMenu(updated);
  };

  const saveReviewSettings = async () => {
    setSavingReviews(true);
    try {
      await axios.put(`${API}/settings/reviews`, reviewSettings, {
        withCredentials: true
      });
      setReviewsSaved(true);
      setTimeout(() => setReviewsSaved(false), 3000);
    } catch (error) {
      console.error('Error saving review settings:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingReviews(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/messages/${messageId}/read`, {}, {
        withCredentials: true
      });
      loadMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;
    try {
      await axios.delete(`${API}/contact/messages/${messageId}`, {
        withCredentials: true
      });
      setSelectedMessage(null);
      loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleCreatePromo = async () => {
    try {
      await axios.post(`${API}/promos/`, promoForm, {
        withCredentials: true
      });
      setShowPromoForm(false);
      setPromoForm({ code: '', discount_percent: 10, description: '', is_unlimited: true, color_theme: 'gold', expires_at: '' });
      loadPromoCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdatePromo = async () => {
    try {
      await axios.put(`${API}/promos/${editingPromo.promo_id}`, promoForm, {
        withCredentials: true
      });
      setEditingPromo(null);
      setPromoForm({ code: '', discount_percent: 10, description: '', is_unlimited: true, color_theme: 'gold', expires_at: '' });
      loadPromoCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce code promo ?')) return;
    try {
      await axios.delete(`${API}/promos/${promoId}`, {
        withCredentials: true
      });
      loadPromoCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleTogglePromoStatus = async (promo) => {
    try {
      await axios.put(`${API}/promos/${promo.promo_id}`, {
        is_active: !promo.is_active
      }, {
        withCredentials: true
      });
      loadPromoCodes();
    } catch (error) {
      console.error('Error toggling promo status:', error);
    }
  };

  const startEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      code: promo.code,
      discount_percent: promo.discount_percent,
      description: promo.description || '',
      is_unlimited: promo.is_unlimited !== false,
      color_theme: promo.color_theme || 'gold',
      expires_at: promo.expires_at || ''
    });
    setShowPromoForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#ECEC75] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-black">Chargement...</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "#D4AF37" }) => (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-black p-3 rounded-lg">
              <Icon size={24} style={{ color }} />
            </div>
            <h3 className="text-sm font-semibold text-black/60 uppercase tracking-wide">
              {title}
            </h3>
          </div>
          <div className="text-4xl font-bold text-black mb-1">{value}</div>
          {subtitle && <div className="text-sm text-black/60">{subtitle}</div>}
        </div>
      </div>
    </div>
  );

  const deviceData = analytics?.device_breakdown
    ? Object.entries(analytics.device_breakdown).map(([name, value]) => ({
        name: name === 'mobile' ? 'Mobile' : 'Desktop',
        value
      }))
    : [];

  const ctaData = analytics?.cta_breakdown
    ? Object.entries(analytics.cta_breakdown).map(([name, value]) => ({
        name: formatCtaName(name),
        clicks: value
      })).sort((a, b) => b.clicks - a.clicks)
    : [];

  function formatCtaName(name) {
    const labels = {
      'order_uber': 'Uber Eats',
      'order_deliveroo': 'Deliveroo',
      'call': 'Appeler',
      'directions': 'Itinéraire',
      'nav_accueil': 'Section Accueil',
      'nav_menu': 'Section Menu',
      'nav_services': 'Section Services',
      'nav_avis': 'Section Avis',
      'nav_contact': 'Section Contact',
      'social_snapchat': 'Snapchat',
      'social_facebook': 'Facebook',
      'social_instagram': 'Instagram',
      'social_tiktok': 'TikTok',
      'carte_compass': 'Carte Compass',
      'footer_uber_eats': 'Footer Uber Eats',
      'footer_deliveroo': 'Footer Deliveroo'
    };
    return labels[name] || name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ECEC75] via-[#e6e67c] to-[#ECEC75]">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-md shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_cosmos-tacos/artifacts/m0583hsq_Le%20Cosmos%20Logo.jpg"
                alt="Le Cosmos"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Dashboard Admin
                </h1>
                <p className="text-sm text-white/70">Le Cosmos Saint-Étienne</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-white hover:text-[#D4AF37] transition-colors"
              >
                <Home size={20} />
                <span>Site web</span>
              </button>
              
              <div className="flex items-center space-x-3 bg-white/10 rounded-lg px-4 py-2">
                <img
                  src={user?.picture || 'https://via.placeholder.com/40'}
                  alt={user?.name}
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-white font-medium">{user?.name}</span>
              </div>

              <button
                onClick={logout}
                className="flex items-center space-x-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg font-semibold hover:bg-[#C5A028] transition-colors"
              >
                <LogOut size={18} />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="mb-8 flex space-x-4 flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'analytics'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <TrendingUp size={20} />
              <span>Statistiques</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'promos'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Tag size={20} />
              <span>Codes Promo</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all relative ${
              activeTab === 'messages'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Mail size={20} />
              <span>Messages</span>
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'settings'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Settings size={20} />
              <span>Avis Google</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'menu'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <UtensilsCrossed size={20} />
              <span>Menu</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('hours')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'hours'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-black/10'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Clock size={20} />
              <span>Horaires</span>
            </div>
          </button>
        </div>

        {activeTab === 'analytics' && (
          <>
            {/* Period Selector */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Statistiques & Analytics
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {['daily', 'weekly', 'monthly', 'yearly'].map((p) => (
                    <button
                      key={p}
                      onClick={() => selectPeriod(p)}
                      data-testid={`period-${p}`}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                        period === p && !customDate
                          ? 'bg-black text-white'
                          : 'bg-white text-black hover:bg-black/10'
                      }`}
                    >
                      {p === 'daily' && 'Aujourd\'hui'}
                      {p === 'weekly' && 'Semaine'}
                      {p === 'monthly' && 'Mois'}
                      {p === 'yearly' && 'Année'}
                    </button>
                  ))}
                  <div className="h-6 w-px bg-black/20 mx-1 hidden md:block" />
                  <div className="relative">
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => selectCustomDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      data-testid="custom-date-picker"
                      className={`px-3 py-2 rounded-lg border font-semibold text-sm transition-all ${
                        customDate
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-black'
                          : 'border-black/20 bg-white text-black/60'
                      } focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none`}
                    />
                  </div>
                </div>
              </div>
              {customDate && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-black/60">
                    Données du {new Date(customDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    onClick={() => selectPeriod('daily')}
                    className="text-xs text-[#D4AF37] hover:underline font-semibold"
                  >
                    Retour à aujourd'hui
                  </button>
                </div>
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                icon={Users}
                title="Visiteurs"
                value={analytics?.total_visitors || 0}
                subtitle="Visiteurs uniques"
                color="#D4AF37"
              />
              <StatCard
                icon={Eye}
                title="Pages vues"
                value={analytics?.total_page_views || 0}
                subtitle="Total de pages vues"
                color="#D4AF37"
              />
              <StatCard
                icon={MousePointerClick}
                title="Clics CTA"
                value={analytics?.total_cta_clicks || 0}
                subtitle="Actions importantes"
                color="#D4AF37"
              />
              <StatCard
                icon={TrendingUp}
                title="Taux de conversion"
                value={
                  analytics?.total_visitors > 0
                    ? `${((analytics.total_cta_clicks / analytics.total_visitors) * 100).toFixed(1)}%`
                    : '0%'
                }
                subtitle="CTA / Visiteurs"
                color="#D4AF37"
              />
            </div>

            {/* Traffic Chart */}
            {(periodData || (customDate && analytics?.hourly_traffic)) && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8">
                <h3 className="text-xl font-bold text-black mb-4">
                  Évolution du trafic
                  <span className="text-sm font-normal text-black/50 ml-2">
                    ({(period === 'daily' || customDate) ? 'par heure' : period === 'yearly' ? 'par mois' : 'par jour'})
                  </span>
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  {(period === 'daily' || customDate) && analytics?.hourly_traffic ? (
                    <BarChart data={analytics.hourly_traffic}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => [`${value} visites`, 'Trafic']}
                        labelFormatter={(label) => `À ${label}`}
                      />
                      <Bar dataKey="visits" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : periodData ? (
                    <LineChart data={periodData.data_points}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="visitors" stroke="#D4AF37" name="Visiteurs" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="page_views" stroke="#0f172a" name="Pages vues" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="cta_clicks" stroke="#22c55e" name="Clics CTA" strokeWidth={2} dot={false} />
                    </LineChart>
                  ) : null}
                </ResponsiveContainer>
              </div>
            )}

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* CTA Breakdown */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">Clics par CTA</h3>
                {ctaData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ctaData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="clicks" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-black/60 text-center py-12">Aucune donnée pour cette période</p>
                )}
              </div>

              {/* Device Breakdown */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">Appareils</h3>
                {deviceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-black/60 text-center py-12">Aucune donnée pour cette période</p>
                )}
              </div>

              {/* Top Pages */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 lg:col-span-2">
                <h3 className="text-xl font-bold text-black mb-4">Pages populaires</h3>
                <div className="space-y-3">
                  {analytics?.top_pages?.map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-black/5 rounded-lg">
                      <span className="text-black font-medium truncate flex-1">{page.url || '/'}</span>
                      <span className="text-[#D4AF37] font-bold ml-4">{page.views} vues</span>
                    </div>
                  ))}
                  {(!analytics?.top_pages || analytics.top_pages.length === 0) && (
                    <p className="text-black/60 text-center py-4">Aucune donnée pour cette période</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'promos' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                Gestion des Codes Promo
              </h2>
              <button
                onClick={() => {
                  setEditingPromo(null);
                  setPromoForm({ code: '', discount_percent: 10, description: '', is_unlimited: true, color_theme: 'gold', expires_at: '' });
                  setShowPromoForm(true);
                }}
                className="flex items-center space-x-2 bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors"
              >
                <Plus size={20} />
                <span>Nouveau Code</span>
              </button>
            </div>

            {/* Promo Form Modal */}
            {showPromoForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    {editingPromo ? 'Modifier le code promo' : 'Créer un code promo'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-black/70 mb-2">
                        Code promo
                      </label>
                      <input
                        type="text"
                        value={promoForm.code}
                        onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                        placeholder="Ex: COSMOS10"
                        className="w-full px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-black/70 mb-2">
                        Réduction (%)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={promoForm.discount_percent}
                        onChange={(e) => setPromoForm({ ...promoForm, discount_percent: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-black/70 mb-2">
                        Description (optionnel)
                      </label>
                      <textarea
                        value={promoForm.description}
                        onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                        placeholder="Ex: Offre de bienvenue"
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none resize-none"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-black/5 rounded-xl">
                      <div>
                        <div className="font-semibold text-black">Offre illimitée</div>
                        <div className="text-sm text-black/60">Le code peut être utilisé sans limite</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPromoForm({ ...promoForm, is_unlimited: !promoForm.is_unlimited })}
                        className={`relative w-12 h-6 rounded-full transition-colors ${promoForm.is_unlimited ? 'bg-[#D4AF37]' : 'bg-black/20'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${promoForm.is_unlimited ? 'translate-x-6' : ''}`} />
                      </button>
                    </div>

                    {/* Color Theme Picker */}
                    <div>
                      <label className="block text-sm font-semibold text-black/70 mb-2">Thème couleur</label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { id: 'gold', label: 'Or', color: '#D4AF37' },
                          { id: 'rose', label: 'Rose', color: '#FF6B9D' },
                          { id: 'green', label: 'Vert', color: '#4CAF50' },
                          { id: 'blue', label: 'Bleu', color: '#2196F3' },
                          { id: 'red', label: 'Rouge', color: '#FF4444' },
                          { id: 'purple', label: 'Violet', color: '#9C27B0' },
                        ].map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setPromoForm({ ...promoForm, color_theme: t.id })}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm font-medium"
                            style={{
                              borderColor: promoForm.color_theme === t.id ? t.color : 'rgba(0,0,0,0.1)',
                              background: promoForm.color_theme === t.id ? `${t.color}15` : 'transparent',
                            }}
                          >
                            <span className="w-4 h-4 rounded-full" style={{ background: t.color }} />
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Expiration */}
                    <div>
                      <label className="block text-sm font-semibold text-black/70 mb-2">Expiration (optionnel)</label>
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <label className="block text-xs text-black/50 mb-1">Date</label>
                          <input
                            type="date"
                            value={promoForm.expires_at ? promoForm.expires_at.slice(0, 10) : ''}
                            min={new Date().toISOString().split('T')[0]}
                            onChange={(e) => {
                              const date = e.target.value;
                              if (!date) {
                                setPromoForm({ ...promoForm, expires_at: '' });
                                return;
                              }
                              const existing = promoForm.expires_at ? new Date(promoForm.expires_at) : new Date();
                              const h = String(existing.getHours()).padStart(2, '0');
                              const m = String(existing.getMinutes()).padStart(2, '0');
                              setPromoForm({ ...promoForm, expires_at: new Date(`${date}T${h}:${m}:00`).toISOString() });
                            }}
                            className="w-full px-3 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none"
                          />
                        </div>
                        <div className="w-24">
                          <label className="block text-xs text-black/50 mb-1">Heure</label>
                          <select
                            value={promoForm.expires_at ? new Date(promoForm.expires_at).getHours() : ''}
                            onChange={(e) => {
                              const h = parseInt(e.target.value);
                              const d = promoForm.expires_at ? new Date(promoForm.expires_at) : new Date();
                              d.setHours(h);
                              setPromoForm({ ...promoForm, expires_at: d.toISOString() });
                            }}
                            disabled={!promoForm.expires_at}
                            className="w-full px-2 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none bg-white disabled:opacity-40"
                          >
                            <option value="">--</option>
                            {[...Array(24)].map((_, i) => (
                              <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                            ))}
                          </select>
                        </div>
                        <div className="w-24">
                          <label className="block text-xs text-black/50 mb-1">Min</label>
                          <select
                            value={promoForm.expires_at ? new Date(promoForm.expires_at).getMinutes() : ''}
                            onChange={(e) => {
                              const m = parseInt(e.target.value);
                              const d = promoForm.expires_at ? new Date(promoForm.expires_at) : new Date();
                              d.setMinutes(m);
                              setPromoForm({ ...promoForm, expires_at: d.toISOString() });
                            }}
                            disabled={!promoForm.expires_at}
                            className="w-full px-2 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none bg-white disabled:opacity-40"
                          >
                            <option value="">--</option>
                            {[0, 15, 30, 45].map((m) => (
                              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {promoForm.expires_at && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-black/50">
                            Expire le {new Date(promoForm.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à {String(new Date(promoForm.expires_at).getHours()).padStart(2, '0')}h{String(new Date(promoForm.expires_at).getMinutes()).padStart(2, '0')}
                          </span>
                          <button
                            type="button"
                            onClick={() => setPromoForm({ ...promoForm, expires_at: '' })}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-4 mt-6">
                    <button
                      onClick={() => {
                        setShowPromoForm(false);
                        setEditingPromo(null);
                      }}
                      className="flex-1 px-4 py-3 rounded-xl border border-black/20 font-semibold hover:bg-black/5 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={editingPromo ? handleUpdatePromo : handleCreatePromo}
                      className="flex-1 bg-[#D4AF37] text-black px-4 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors"
                    >
                      {editingPromo ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Promo Codes List */}
            <div className="grid gap-4">
              {promoCodes.length === 0 ? (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center">
                  <Tag size={48} className="mx-auto text-black/30 mb-4" />
                  <p className="text-xl text-black/60">Aucun code promo créé</p>
                  <p className="text-black/40 mt-2">Cliquez sur "Nouveau Code" pour commencer</p>
                </div>
              ) : (
                promoCodes.map((promo) => (
                  <div
                    key={promo.promo_id}
                    className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 transition-all ${
                      !promo.is_active ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="p-4 rounded-xl" style={{ background: promo.is_active ? (({gold:'#D4AF37',rose:'#FF6B9D',green:'#4CAF50',blue:'#2196F3',red:'#FF4444',purple:'#9C27B0'})[promo.color_theme] || '#D4AF37') : 'rgba(0,0,0,0.1)' }}>
                          <Percent size={24} className={promo.is_active ? 'text-black' : 'text-black/50'} />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                            <span className="text-2xl font-bold text-black">{promo.code}</span>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              promo.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {promo.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                          <p className="text-black/60 mt-1">
                            {promo.discount_percent}% de réduction
                            {promo.description && ` • ${promo.description}`}
                          </p>
                          <div className="flex items-center space-x-2 mt-1 flex-wrap gap-y-1">
                            {promo.is_unlimited !== false ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
                                Illimité
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                                Limité
                              </span>
                            )}
                            {promo.color_theme && promo.color_theme !== 'gold' && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-black/5 text-black/60">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: ({rose:'#FF6B9D',green:'#4CAF50',blue:'#2196F3',red:'#FF4444',purple:'#9C27B0'})[promo.color_theme] || '#D4AF37' }} />
                                {({rose:'Rose',green:'Vert',blue:'Bleu',red:'Rouge',purple:'Violet'})[promo.color_theme]}
                              </span>
                            )}
                            {promo.expires_at && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                Expire: {new Date(promo.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleTogglePromoStatus(promo)}
                          className={`p-2 rounded-lg transition-colors ${
                            promo.is_active 
                              ? 'hover:bg-red-100 text-red-600' 
                              : 'hover:bg-green-100 text-green-600'
                          }`}
                          title={promo.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {promo.is_active ? <X size={20} /> : <Check size={20} />}
                        </button>
                        <button
                          onClick={() => startEditPromo(promo)}
                          className="p-2 rounded-lg hover:bg-black/10 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 size={20} className="text-black/70" />
                        </button>
                        <button
                          onClick={() => handleDeletePromo(promo.promo_id)}
                          className="p-2 rounded-lg hover:bg-red-100 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={20} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                Messages de Contact
              </h2>
              <div className="text-black/60">
                {unreadCount > 0 ? `${unreadCount} message(s) non lu(s)` : 'Tous les messages sont lus'}
              </div>
            </div>

            {messages.length === 0 ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center">
                <Mail size={48} className="mx-auto text-black/30 mb-4" />
                <p className="text-xl text-black/60">Aucun message reçu</p>
                <p className="text-black/40 mt-2">Les messages du formulaire de contact apparaîtront ici</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Messages List */}
                <div className="lg:col-span-1 space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      onClick={() => {
                        setSelectedMessage(msg);
                        if (!msg.is_read) markAsRead(msg.message_id);
                      }}
                      className={`bg-white/90 backdrop-blur-sm rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                        selectedMessage?.message_id === msg.message_id 
                          ? 'ring-2 ring-[#D4AF37]' 
                          : ''
                      } ${!msg.is_read ? 'border-l-4 border-[#D4AF37]' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {!msg.is_read ? (
                            <Circle size={10} className="fill-[#D4AF37] text-[#D4AF37]" />
                          ) : (
                            <CheckCircle size={14} className="text-green-500" />
                          )}
                          <span className={`font-semibold ${!msg.is_read ? 'text-black' : 'text-black/70'}`}>
                            {msg.name}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-black/60 truncate">{msg.message}</p>
                      <p className="text-xs text-black/40 mt-2">
                        {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Message Detail */}
                <div className="lg:col-span-2">
                  {selectedMessage ? (
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h3 className="text-2xl font-bold text-black">{selectedMessage.name}</h3>
                          <p className="text-black/60">
                            {new Date(selectedMessage.created_at).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteMessage(selectedMessage.message_id)}
                          className="p-2 rounded-lg hover:bg-red-100 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={20} className="text-red-600" />
                        </button>
                      </div>

                      <div className="space-y-4 mb-6">
                        <div className="flex items-center space-x-3 text-black/80">
                          <Mail size={18} className="text-[#D4AF37]" />
                          <a href={`mailto:${selectedMessage.email}`} className="hover:text-[#D4AF37] transition-colors">
                            {selectedMessage.email}
                          </a>
                        </div>
                        {selectedMessage.phone && (
                          <div className="flex items-center space-x-3 text-black/80">
                            <Phone size={18} className="text-[#D4AF37]" />
                            <a href={`tel:${selectedMessage.phone}`} className="hover:text-[#D4AF37] transition-colors">
                              {selectedMessage.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-black/10 pt-6">
                        <h4 className="font-semibold text-black mb-3">Message :</h4>
                        <p className="text-black/80 whitespace-pre-wrap leading-relaxed bg-black/5 p-4 rounded-xl">
                          {selectedMessage.message}
                        </p>
                      </div>

                      <div className="flex space-x-3 mt-6">
                        <a
                          href={`mailto:${selectedMessage.email}?subject=Re: Message de ${selectedMessage.name} - Le Cosmos Tacos`}
                          className="flex-1 bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors text-center"
                        >
                          Répondre par email
                        </a>
                        {selectedMessage.phone && (
                          <a
                            href={`tel:${selectedMessage.phone}`}
                            className="bg-black text-white px-6 py-3 rounded-xl font-semibold hover:bg-black/80 transition-colors"
                          >
                            Appeler
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center">
                      <MessageSquare size={48} className="mx-auto text-black/30 mb-4" />
                      <p className="text-xl text-black/60">Sélectionnez un message</p>
                      <p className="text-black/40 mt-2">Cliquez sur un message pour voir les détails</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'settings' && (
          <div>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                Paramètres Avis Google
              </h2>
              <p className="text-black/60 mt-2">
                Gérez les statistiques affichées dans la section Avis de votre site
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-lg">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-black/70 mb-2" htmlFor="total-reviews">
                    Nombre total d'avis
                  </label>
                  <input
                    id="total-reviews"
                    data-testid="review-count-input"
                    type="number"
                    min="0"
                    value={reviewSettings.total_reviews}
                    onChange={(e) => setReviewSettings({ ...reviewSettings, total_reviews: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none text-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black/70 mb-2" htmlFor="avg-rating">
                    Note moyenne (0 à 5)
                  </label>
                  <input
                    id="avg-rating"
                    data-testid="review-rating-input"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={reviewSettings.average_rating}
                    onChange={(e) => setReviewSettings({ ...reviewSettings, average_rating: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none text-lg"
                  />
                </div>

                {/* Preview */}
                <div className="bg-black/5 rounded-xl p-4">
                  <p className="text-sm font-semibold text-black/60 mb-2">Aperçu :</p>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={24}
                          className={i < Math.floor(reviewSettings.average_rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'fill-black/20 text-black/20'}
                        />
                      ))}
                    </div>
                    <span className="text-xl font-bold text-black">{reviewSettings.average_rating}/5</span>
                    <span className="text-black/60">({reviewSettings.total_reviews} avis)</span>
                  </div>
                </div>

                <button
                  data-testid="save-reviews-btn"
                  onClick={saveReviewSettings}
                  disabled={savingReviews}
                  className="w-full flex items-center justify-center space-x-2 bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors disabled:opacity-50"
                >
                  {savingReviews ? (
                    <span>Enregistrement...</span>
                  ) : reviewsSaved ? (
                    <>
                      <CheckCircle size={20} />
                      <span>Enregistré !</span>
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      <span>Enregistrer les modifications</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'menu' && editingMenu && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Gestion du Menu
                </h2>
                <p className="text-black/60 mt-2">Modifiez les prix et les détails de vos produits</p>
              </div>
              <button
                data-testid="save-menu-btn"
                onClick={saveMenuData}
                disabled={savingMenu}
                className="flex items-center space-x-2 bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors disabled:opacity-50"
              >
                {savingMenu ? <span>Enregistrement...</span> : menuSaved ? (
                  <><CheckCircle size={20} /><span>Enregistré !</span></>
                ) : (
                  <><Save size={20} /><span>Enregistrer le menu</span></>
                )}
              </button>
            </div>

            {/* Menu Midi */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-black mb-4">Menu Midi</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black/70 mb-1">Nom</label>
                  <input type="text" value={editingMenu.lunchDeal?.name || ''} onChange={(e) => setEditingMenu({...editingMenu, lunchDeal: {...editingMenu.lunchDeal, name: e.target.value}})} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-black/70 mb-1">Prix (€)</label>
                  <input type="number" step="0.10" value={editingMenu.lunchDeal?.price || 0} onChange={(e) => setEditingMenu({...editingMenu, lunchDeal: {...editingMenu.lunchDeal, price: parseFloat(e.target.value) || 0}})} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-black/70 mb-1">Description</label>
                  <input type="text" value={editingMenu.lunchDeal?.description || ''} onChange={(e) => setEditingMenu({...editingMenu, lunchDeal: {...editingMenu.lunchDeal, description: e.target.value}})} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
            </div>

            {/* Tacos Formats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-black mb-4">Tacos / Roulés - Formats</h3>
              <div className="space-y-3">
                {editingMenu.tacos?.formats?.map((f, i) => (
                  <div key={i} className="grid grid-cols-3 gap-3 items-center bg-black/5 p-3 rounded-lg">
                    <div>
                      <label className="block text-xs text-black/60">Nom</label>
                      <input type="text" value={f.name} onChange={(e) => {
                        const formats = [...editingMenu.tacos.formats];
                        formats[i] = {...formats[i], name: e.target.value};
                        setEditingMenu({...editingMenu, tacos: {...editingMenu.tacos, formats}});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-black/60">Description</label>
                      <input type="text" value={f.description} onChange={(e) => {
                        const formats = [...editingMenu.tacos.formats];
                        formats[i] = {...formats[i], description: e.target.value};
                        setEditingMenu({...editingMenu, tacos: {...editingMenu.tacos, formats}});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-black/60">Prix (€)</label>
                      <input type="number" step="0.10" value={f.price} onChange={(e) => {
                        const formats = [...editingMenu.tacos.formats];
                        formats[i] = {...formats[i], price: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, tacos: {...editingMenu.tacos, formats}});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-4">
                <div>
                  <label className="block text-xs text-black/60">Option Menu (+€)</label>
                  <input type="number" step="0.10" value={editingMenu.tacos?.menuOption || 0} onChange={(e) => setEditingMenu({...editingMenu, tacos: {...editingMenu.tacos, menuOption: parseFloat(e.target.value) || 0}})} className="px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm w-32" />
                </div>
                <div>
                  <label className="block text-xs text-black/60">Supplément (+€)</label>
                  <input type="number" step="0.10" value={editingMenu.tacos?.supplementPrice || 0} onChange={(e) => setEditingMenu({...editingMenu, tacos: {...editingMenu.tacos, supplementPrice: parseFloat(e.target.value) || 0}})} className="px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm w-32" />
                </div>
              </div>
            </div>

            {/* Burgers */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-black mb-4">Burgers</h3>
              <div className="space-y-3">
                {editingMenu.burgers?.map((b, i) => (
                  <div key={i} className="grid grid-cols-4 gap-3 items-center bg-black/5 p-3 rounded-lg">
                    <div>
                      <label className="block text-xs text-black/60">Nom</label>
                      <input type="text" value={b.name} onChange={(e) => {
                        const burgers = [...editingMenu.burgers];
                        burgers[i] = {...burgers[i], name: e.target.value};
                        setEditingMenu({...editingMenu, burgers});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-black/60">Description</label>
                      <input type="text" value={b.description} onChange={(e) => {
                        const burgers = [...editingMenu.burgers];
                        burgers[i] = {...burgers[i], description: e.target.value};
                        setEditingMenu({...editingMenu, burgers});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-black/60">Prix Seul (€)</label>
                      <input type="number" step="0.10" value={b.priceSeul} onChange={(e) => {
                        const burgers = [...editingMenu.burgers];
                        burgers[i] = {...burgers[i], priceSeul: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, burgers});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-black/60">Prix Menu (€)</label>
                      <input type="number" step="0.10" value={b.priceMenu} onChange={(e) => {
                        const burgers = [...editingMenu.burgers];
                        burgers[i] = {...burgers[i], priceMenu: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, burgers});
                      }} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sharing / Kids / Desserts / Drinks */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Sharing */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">À Partager</h3>
                <div className="space-y-2">
                  {editingMenu.sharing?.map((item, i) => (
                    <div key={i} className="flex gap-3 items-center bg-black/5 p-2 rounded-lg">
                      <input type="text" value={item.name} onChange={(e) => {
                        const sharing = [...editingMenu.sharing];
                        sharing[i] = {...sharing[i], name: e.target.value};
                        setEditingMenu({...editingMenu, sharing});
                      }} className="flex-1 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <input type="number" step="0.10" value={item.price} onChange={(e) => {
                        const sharing = [...editingMenu.sharing];
                        sharing[i] = {...sharing[i], price: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, sharing});
                      }} className="w-24 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <span className="text-sm text-black/60">€</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desserts */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">Desserts</h3>
                <div className="space-y-2">
                  {editingMenu.desserts?.map((item, i) => (
                    <div key={i} className="flex gap-3 items-center bg-black/5 p-2 rounded-lg">
                      <input type="text" value={item.name} onChange={(e) => {
                        const desserts = [...editingMenu.desserts];
                        desserts[i] = {...desserts[i], name: e.target.value};
                        setEditingMenu({...editingMenu, desserts});
                      }} className="flex-1 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <input type="number" step="0.10" value={item.price} onChange={(e) => {
                        const desserts = [...editingMenu.desserts];
                        desserts[i] = {...desserts[i], price: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, desserts});
                      }} className="w-24 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <span className="text-sm text-black/60">€</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Drinks */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">Boissons</h3>
                <div className="space-y-2">
                  {editingMenu.drinks?.map((item, i) => (
                    <div key={i} className="flex gap-3 items-center bg-black/5 p-2 rounded-lg">
                      <input type="text" value={item.name} onChange={(e) => {
                        const drinks = [...editingMenu.drinks];
                        drinks[i] = {...drinks[i], name: e.target.value};
                        setEditingMenu({...editingMenu, drinks});
                      }} className="flex-1 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <input type="number" step="0.10" value={item.price} onChange={(e) => {
                        const drinks = [...editingMenu.drinks];
                        drinks[i] = {...drinks[i], price: parseFloat(e.target.value) || 0};
                        setEditingMenu({...editingMenu, drinks});
                      }} className="w-24 px-2 py-1 rounded border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                      <span className="text-sm text-black/60">€</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kids */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-black mb-4">Menu Enfant</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-black/60 mb-1">Contenu</label>
                    <input type="text" value={editingMenu.kids?.items || ''} onChange={(e) => setEditingMenu({...editingMenu, kids: {...editingMenu.kids, items: e.target.value}})} className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-black/60 mb-1">Prix (€)</label>
                    <input type="number" step="0.10" value={editingMenu.kids?.price || 0} onChange={(e) => setEditingMenu({...editingMenu, kids: {...editingMenu.kids, price: parseFloat(e.target.value) || 0}})} className="w-32 px-3 py-2 rounded-lg border border-black/20 focus:border-[#D4AF37] outline-none text-sm" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'hours' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                  Horaires d'ouverture
                </h2>
                <p className="text-black/60 mt-2">Modifiez les horaires affichés sur le site</p>
              </div>
              <button
                data-testid="save-hours-btn"
                onClick={saveHoursData}
                disabled={savingHours}
                className="flex items-center space-x-2 bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-semibold hover:bg-[#C5A028] transition-colors disabled:opacity-50"
              >
                {savingHours ? <span>Enregistrement...</span> : hoursSaved ? (
                  <><CheckCircle size={20} /><span>Enregistré !</span></>
                ) : (
                  <><Save size={20} /><span>Enregistrer</span></>
                )}
              </button>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 max-w-2xl">
              <p className="text-black/50 text-sm mb-6">
                Écrivez les horaires librement pour chaque jour. Par exemple : "11:00 - 14:30 et 18:00 - 23:30" ou "Fermé"
              </p>
              <div className="space-y-4">
                {DAYS.map((day) => (
                  <div key={day} className="flex items-center gap-4">
                    <label className="w-28 text-black font-semibold capitalize text-right shrink-0">{day}</label>
                    <input
                      type="text"
                      value={hoursData[day] || ''}
                      onChange={(e) => setHoursData({ ...hoursData, [day]: e.target.value })}
                      placeholder="Ex: 11:00 - 14:30 et 18:00 - 23:30"
                      data-testid={`hours-${day}`}
                      className="flex-1 px-4 py-3 rounded-xl border border-black/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
